import { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Button from '@base/Button/Button';
import NextImage from '@base/Image';
import NotifyMe from '@icon/PlatformBatchGroup/NotifyMe';
import NotifiedMe from '@icon/PlatformBatchGroup/NotifiedMe';
import NotifiedMeDarkMode from '@icon/PlatformBatchGroup/NotifiedMeDarkMode';
import P2 from '@base/P2/P2';
import H5 from '@base/H5/H5';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import useNotifyMe from '../useNotifyMe';
import notificationsType from '../constants';

export const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column: 1/25;
  grid-template-columns: 56px auto 202px;
  grid-column-gap: ${Spacings.SPACING_6B};
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_10B};
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  margin: -${Spacings.SPACING_1B} ${Spacings.SPACING_0B} ${Spacings.SPACING_6B} ${Spacings.SPACING_0B};
  border-radius: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    grid-template-columns: auto 56px;
    grid-column-gap: ${Spacings.SPACING_0B};
    grid-row-gap: ${Spacings.SPACING_0B};
    margin: -${Spacings.SPACING_2B} auto ${Spacings.SPACING_4B} auto;
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_0B};
    border-radius: unset;
    border-left: none;
    border-right: none;
    min-width: 328px;
    height: 162px;
    width: 100%;
  }
`;

export const Title = styled(H5)`
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.H4};
  line-height: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    width: 202px;
    text-align: left;
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.LARGE};
    line-height: ${Spacings.SPACING_5B};
    margin-bottom: ${Spacings.SPACING_1B};
  }
`;
export const Details = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${mediaQuery.uptoMobile} {
    display: grid;
    grid-column: 1/2;
    grid-row: 1/2;
    display: flex;
    flex-direction: column;
  }
`;

export const Info = styled(P2)`
  font-size: ${Fonts.SMALL};
  line-height: ${Spacings.SPACING_5B};
  color: var(--color-text-secondary);
  ${mediaQuery.uptoMobile} {
    width: 244px;
    text-align: left;
    font-size: ${Fonts.ULTRA_SMALL};
    line-height: ${Spacings.SPACING_18};
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

export const NotifiedMeCTA = styled(Button)`
  background-color: ${({ $isNotified }) =>
    $isNotified ? 'var(--color-divider)' : 'var(--color-i-black)'};
  color: ${({ $isNotified }) =>
    $isNotified ? 'var(--color-text-primary)' : 'var(--color-i-white)'};
  padding: ${Spacings.SPACING_0B};
  font-size: ${Fonts.SMALL};
  height: 48px;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '202px')};
  border-radius: ${Spacings.SPACING_2B};
  &:active {
    background-color: var(--color-i-black);
  }
  ${mediaQuery.uptoMobile} {
    div {
      align-items: center;
      justify-content: center;
      display: flex;
      margin-right: ${Spacings.SPACING_1B};
    }
    height: 32px;
    font-size: ${Fonts.ULTRA_SMALL};
    line-height: ${Spacings.SPACING_4B};
    width: 132px;
    min-height: 32px;
    border-radius: ${Spacings.SPACING_1B};
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '202px')};
  ${mediaQuery.uptoMobile} {
    height: 32px;
    display: flex;
    justify-content: flex-start;
    width: 132px;
    svg {
      height: 20px !important;
      width: 20px !important;
    }
  }
`;

export const NotifyWrapper = styled.div`
  grid-column: 1/25;
  ${mediaQuery.uptoMobile} {
    height: 162px;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;
export const IconWrapper = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  ${mediaQuery.uptoMobile} {
    grid-column: 2/3;
  }
`;

const getIcon = ({ isNotified, isDarkModeOn }) => {
  if (isNotified && isDarkModeOn)
    return <NotifiedMeDarkMode size="24px" color="var(--color-base-1)" />;
  if (isNotified && !isDarkModeOn)
    return <NotifiedMe size="24px" color="var(--color-base-1)" />;
  return <NotifyMe size="24px" color="var(--color-base-1)" />;
};

const WebNotifyMe = () => {
  const {
    sendNotifyMeRequest,
    notifiedNotification = {},
    notifyMeInfo,
    isNotifyRequested,
    isNotificationFetched
  } = useNotifyMe();
  const { onBlock } = notifyMeInfo;
  const isNotified = notifiedNotification[notificationsType.UPCOMING_BATCHES];

  const handleClick = useCallback(() => {
    sendNotifyMeRequest({
      notification_type: notificationsType.UPCOMING_BATCHES,
      is_active: !isNotified,
      touchPoint: 'Page Header'
    });
  }, [isNotified, sendNotifyMeRequest]);

  return (
    <NotifyWrapper>
      <Wrapper>
        <IconWrapper>
          <NextImage src="special-class.svg" alt="notify-me" layout="fill" />
        </IconWrapper>
        <Details>
          <Title>{onBlock.title}</Title>
          <Info>{onBlock.subtitle}</Info>
        </Details>
        {isNotificationFetched && (
          <ButtonWrapper>
            <NotifiedMeCTAWrapper
              isNotified={isNotified}
              onClick={handleClick}
              isNotifyRequested={isNotifyRequested}
            />
          </ButtonWrapper>
        )}
      </Wrapper>
    </NotifyWrapper>
  );
};
export default WebNotifyMe;

export const NotifiedMeCTAWrapper = ({
  onClick,
  isNotified,
  fullWidth = false,
  isNotifyRequested
}) => {
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const icon = useMemo(
    () => getIcon({ isNotified, isDarkModeOn }),
    [isDarkModeOn, isNotified]
  );

  return (
    <NotifiedMeCTA
      $isNotified={isNotified}
      $fullWidth={fullWidth}
      label={isNotified ? 'We’ll notify you' : 'Notify me'}
      variant={isNotifyRequested ? 'disabled' : 'filled'}
      onClick={onClick}
      icon={icon}
    />
  );
};
